import theme from 'ThemeProvider/theme';
import sx from 'utils/createSx';

export default sx({
  container: {
    minHeight: '100vh',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },

  // Barra de busca e filtros
  searchAndFilterBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: 'center',
  },
  searchField: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.grey[300],
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  mobileFilterButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
  },

  // Pills de estilos (topo)
  stylePills: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: theme.spacing(0.5),
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      height: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: 2,
    },
  },
  stylePill: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    color: theme.palette.text.primary,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
      borderColor: theme.palette.primary.main,
    },
  },
  stylePillActive: {
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.common.white,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
    },
  },

  // Filtros ativos (chips)
  activeFilters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  filterChip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
    },
  },

  // Contador de resultados
  resultsHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resultsCount: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },

  // Sidebar de filtros (desktop)
  sidebarFilters: {
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    position: 'sticky',
    top: theme.spacing(2),
    maxHeight: 'calc(100vh - 200px)',
    overflow: 'auto',
  },

  // Container de filtros
  filtersContainer: {
    padding: theme.spacing(3),
  },
  filtersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filtersTitle: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  clearButton: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    textTransform: 'none',
    padding: theme.spacing(0.5, 1),
  },

  // Seções de filtro
  filterSection: {
    marginBottom: theme.spacing(3),
  },
  filterSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  divider: {
    marginBottom: theme.spacing(3),
  },

  // Slider de preço
  priceSlider: {
    marginBottom: theme.spacing(1),
    '& .MuiSlider-thumb': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .MuiSlider-track': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .MuiSlider-rail': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  priceRange: {
    textAlign: 'center',
  },

  // Lista de filtros
  filterList: {
    maxHeight: '200px',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '2px',
    },
  },
  filterListItem: {
    padding: theme.spacing(0.5, 0),
  },
  formControlLabel: {
    margin: 0,
    width: '100%',
    alignItems: 'center',
  },
  filterCheckbox: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
  },
  filterLabel: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },

  // Cards de produtos
  productCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
    },
  },

  // Selos Sem Álcool / Sem Glúten (canto superior esquerdo, sobre a imagem)
  stampsBadge: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    zIndex: 4,
  },
  stampIcon: {
    width: 60,
    height: 60,
    objectFit: 'contain',
    [theme.breakpoints.down('sm')]: {
      width: 36,
      height: 36,
    },
  },

  // Selo LowCarb + badges (canto superior direito, sobre a imagem)
  stampsBadgeRight: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    zIndex: 4,
  },
  discountBadgeInColumn: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  outOfStockBadgeInColumn: {
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },

  // Badge de fora de estoque
  outOfStockBadge: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.grey[600],
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  outOfStockIcon: {
    fontSize: '0.875rem',
  },
  outOfStockText: {
    fontWeight: 'bold',
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.625rem',
    },
  },

  // Badge de desconto
  discountBadge: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  discountIcon: {
    fontSize: '0.875rem',
  },
  discountText: {
    fontWeight: 'bold',
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.625rem',
    },
  },

  // Imagem do produto
  productImage: {
    objectFit: 'contain',
    backgroundColor: theme.palette.grey[100],
    height: 200,
    [theme.breakpoints.down('sm')]: {
      height: 120,
    },
    [theme.breakpoints.between('sm', 'md')]: {
      height: 160,
    },
  },

  // Conteúdo do produto
  productContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  productName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  productBrewery: {
    marginBottom: theme.spacing(0.5),
    fontSize: '0.875rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },
  productStyle: {
    marginBottom: theme.spacing(2),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.625rem',
      marginBottom: theme.spacing(1),
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },

  // Preços
  priceContainer: {
    marginBottom: theme.spacing(1),
  },
  originalPrice: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  currentPrice: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },

  // Seção de Assinante
  subscriberSection: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    border: `1px solid ${theme.palette.grey[200]}`,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.75),
      marginBottom: theme.spacing(1),
    },
  },
  subscriberLabel: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.625rem',
      marginBottom: theme.spacing(0.25),
    },
  },
  subscriberPrice: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.5),
    },
  },
  subscribeLink: {
    fontSize: '0.75rem',
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
    border: 'none',
    background: 'none',
    padding: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.625rem',
    },
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },

  // Botão adicionar ao carrinho
  addToCartButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      padding: theme.spacing(0.75, 1),
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[600],
    },
  },

  // Sem resultados
  noResults: {
    textAlign: 'center',
    padding: theme.spacing(6),
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  },
  noResultsTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  clearFiltersButton: {
    marginTop: theme.spacing(3),
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },

  // Drawer móvel
  mobileDrawer: {
    '& .MuiDrawer-paper': {
      width: '280px',
      maxWidth: '85vw',
    },
  },
  drawerContent: {
    height: '100%',
    overflow: 'auto',
  },
});
